import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, PhoneIcon, ArrowUpRightIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
function ContactGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    let time = 0;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const spacing = 50;
      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing;
          const y = j * spacing;
          // Radial fade from bottom-right
          const dist = Math.sqrt((x - w) ** 2 + (y - h) ** 2);
          const maxDist = Math.sqrt(w ** 2 + h ** 2);
          const fade = Math.max(0, 1 - dist / (maxDist * 0.7));
          const wave = Math.sin(time * 0.5 + i * 0.4 + j * 0.3) * 0.5 + 0.5;
          const alpha = fade * wave * 0.08;
          if (alpha > 0.005) {
            ctx.beginPath();
            ctx.arc(x, y, 1 + wave, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(250, 250, 250, ${alpha})`;
            ctx.fill();
          }
        }
      }
      time += 0.012;
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-31" />;
}
export function Contact() {
  const {
    t
  } = useLanguage();
  return <section id="contact" className="py-32 md:py-40 px-6 md:px-12 lg:px-20 z-10 relative overflow-hidden" data-id="element-32">
      <div className="max-w-[1400px] mx-auto" data-id="element-33">
        <div className="border-t border-border pt-16 md:pt-24 relative" data-id="element-34">
          {/* Animated dot grid background */}
          <ContactGrid data-id="element-35" />

          {/* Decorative circles */}
          <motion.div className="absolute -right-20 top-20 w-[300px] h-[300px] md:w-[400px] md:h-[400px] rounded-full border border-white/[0.03] pointer-events-none hidden md:block" initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-36" />
          <motion.div className="absolute -right-10 top-32 w-[200px] h-[200px] md:w-[280px] md:h-[280px] rounded-full border border-white/[0.04] pointer-events-none hidden md:block" initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-37" />

          <div className="relative z-10" data-id="element-38">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-39">
              <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-6 font-light" data-id="element-40">
                {t.contactOverline}
              </p>
              <h2 className="text-3xl md:text-6xl lg:text-7xl font-display font-bold text-primary tracking-tight mb-6 leading-[1.1]" data-id="element-41">
                {t.contactTitle1}
                <br data-id="element-42" />
                {t.contactTitle2}
              </h2>

              {/* Animated divider */}
              <motion.div className="w-16 h-px bg-gradient-to-r from-white/30 to-transparent mb-8" initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} style={{
              transformOrigin: 'left'
            }} data-id="element-43" />

              <p className="text-secondary text-lg font-light max-w-md mb-14" data-id="element-44">
                {t.contactSubtitle}
              </p>
            </motion.div>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mb-16" data-id="element-45">
              <motion.a href="mailto:zanikicu@gmail.com" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2,
              ease: [0.16, 1, 0.3, 1]
            }} className="group relative p-6 rounded-xl border border-border bg-surface/50 backdrop-blur-sm hover:border-secondary/30 hover:bg-surface transition-all duration-500" data-id="element-46">
                <div className="flex items-start justify-between mb-4" data-id="element-47">
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-500" data-id="element-48">
                    <MailIcon className="w-4 h-4 text-secondary group-hover:text-primary transition-colors duration-500" data-id="element-49" />
                  </div>
                  <ArrowUpRightIcon className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-1 group-hover:translate-y-0" data-id="element-50" />
                </div>
                <p className="text-[11px] text-secondary/50 tracking-[0.15em] uppercase font-light mb-1" data-id="element-51">
                  {t.contactEmail}
                </p>
                <span className="text-primary text-base font-display font-semibold tracking-tight" data-id="element-52">
                  zanikicu@gmail.com
                </span>
              </motion.a>

              <motion.a href="tel:+385914305555" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} className="group relative p-6 rounded-xl border border-border bg-surface/50 backdrop-blur-sm hover:border-secondary/30 hover:bg-surface transition-all duration-500" data-id="element-53">
                <div className="flex items-start justify-between mb-4" data-id="element-54">
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-500" data-id="element-55">
                    <PhoneIcon className="w-4 h-4 text-secondary group-hover:text-primary transition-colors duration-500" data-id="element-56" />
                  </div>
                  <ArrowUpRightIcon className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-1 group-hover:translate-y-0" data-id="element-57" />
                </div>
                <p className="text-[11px] text-secondary/50 tracking-[0.15em] uppercase font-light mb-1" data-id="element-58">
                  {t.contactPhone}
                </p>
                <span className="text-primary text-base font-display font-semibold tracking-tight" data-id="element-59">
                  +385 91 430 5555
                </span>
              </motion.a>

              <motion.a href="https://wa.me/385914305555" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5,
              ease: [0.16, 1, 0.3, 1]
            }} className="group relative p-6 rounded-xl border border-border bg-surface/50 backdrop-blur-sm hover:border-secondary/30 hover:bg-surface transition-all duration-500" data-id="element-60">
                <div className="flex items-start justify-between mb-4" data-id="element-61">
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-500" data-id="element-62">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-secondary group-hover:text-primary transition-colors duration-500" fill="currentColor" data-id="element-63">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-64" />
                    </svg>
                  </div>
                  <ArrowUpRightIcon className="w-4 h-4 text-muted opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-1 group-hover:translate-y-0" data-id="element-65" />
                </div>
                <p className="text-[11px] text-secondary/50 tracking-[0.15em] uppercase font-light mb-1" data-id="element-66">
                  {t.contactWhatsApp}
                </p>
                <span className="text-primary text-base font-display font-semibold tracking-tight" data-id="element-67">
                  {t.contactWhatsAppCTA}
                </span>
              </motion.a>
            </div>

            {/* Availability badge */}
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-border" data-id="element-68">
              <span className="relative flex h-2 w-2" data-id="element-69">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" data-id="element-71" />
              </span>
              <span className="text-xs text-secondary font-light tracking-wider" data-id="element-72">
                {t.contactAvailable}
              </span>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-32 md:mt-40 flex items-center justify-between text-muted text-xs font-light tracking-wider" data-id="element-73">
          <span data-id="element-74">© {new Date().getFullYear()}</span>
          <span data-id="element-75">{t.contactFooterLocation}</span>
        </div>
      </div>
    </section>;
}